import { Card, Form, Input, Button, Space, Message, Grid, Select, InputNumber, DatePicker, Table, Typography, Divider } from "@arco-design/web-react";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WorkOrderAPI, BOMAPI } from "@/apis/manufacturing";
import Decimal from "decimal.js";
import { QUANTITY_DECIMAL_PLACES } from "@/config";
import { Number } from "@/components/Numeric";

const FormItem = Form.Item;
const { Row, Col } = Grid;
const { Title } = Typography;

export const WorkOrderEdit = () => {
    const params = useParams();
    const woId = params.woId;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [boms, setBoms] = useState([]);
    const [selectedBom, setSelectedBom] = useState(null);
    const [finishedItems, setFinishedItems] = useState([]);
    const [rawItems, setRawItems] = useState([]);

    useEffect(() => {
        const fetchBOMs = async () => {
            const result = await BOMAPI.actives();
            setBoms(result);
        };
        fetchBOMs();
    }, []);

    const calculateItems = (bom, quantity, wasteFactor) => {
        if (!bom) return;
        const qty = quantity || 1;
        const wf = wasteFactor ?? bom.wasteFactor ?? 0;

        const fi = (bom.finishedItems || []).map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            description: item.description,
            quantity: Decimal.mul(item.quantity, qty).toFixed(QUANTITY_DECIMAL_PLACES, Decimal.ROUND_HALF_UP),
            uom: item.uom,
        }));

        const wasteMultiplier = new Decimal(wf || 0).div(100).plus(1);
        const ri = (bom.rawItems || []).map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            description: item.description,
            quantity: Decimal.mul(item.quantity, qty).mul(wasteMultiplier).toFixed(QUANTITY_DECIMAL_PLACES, Decimal.ROUND_HALF_UP),
            uom: item.uom,
        }));

        setFinishedItems(fi);
        setRawItems(ri);
    };

    const getInfo = useCallback(async () => {
        try {
            const values = await WorkOrderAPI.get(woId);
            form.setFieldsValue({
                bomId: values.bomId,
                quantity: values.quantity,
                wasteFactor: values.wasteFactor,
                productionDate: values.productionDate,
                notes: values.notes,
            });

            if (values.bomId) {
                const bom = await BOMAPI.get(values.bomId);
                setSelectedBom(bom);
                calculateItems(bom, values.quantity, values.wasteFactor);
            }
        } catch (error) {
            Message.error(error.message);
        }
    }, [woId, form]);

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    const onBomSelected = async (bomId) => {
        if (!bomId) {
            setSelectedBom(null);
            setFinishedItems([]);
            setRawItems([]);
            return;
        }
        try {
            const bom = await BOMAPI.get(bomId);
            setSelectedBom(bom);
            form.setFieldsValue({
                bomId: bom.id,
                wasteFactor: bom.wasteFactor,
            });
            const currentQuantity = form.getFieldValue("quantity") || 1;
            calculateItems(bom, currentQuantity, bom.wasteFactor);
        } catch (error) {
            Message.error(error.message);
        }
    };

    const onQuantityChange = (value) => {
        if (!selectedBom) return;
        const wf = form.getFieldValue("wasteFactor") ?? selectedBom.wasteFactor ?? 0;
        calculateItems(selectedBom, value || 1, wf);
    };

    const onWasteFactorChange = (value) => {
        if (!selectedBom) return;
        const qty = form.getFieldValue("quantity") || 1;
        calculateItems(selectedBom, qty, value ?? selectedBom.wasteFactor ?? 0);
    };

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            if (!values.bomId) {
                Message.warning("Please select a BOM");
                return;
            }
            if (!values.quantity || values.quantity <= 0) {
                Message.warning("Quantity must be greater than 0");
                return;
            }
            if (!values.productionDate) {
                Message.warning("Production Date is required");
                return;
            }
            setLoading(true);
            const payload = {
                bomId: values.bomId,
                quantity: values.quantity,
                wasteFactor: values.wasteFactor,
                productionDate: values.productionDate,
                notes: values.notes,
            };
            await WorkOrderAPI.update(woId, payload);
            Message.success("Work Order updated successfully");
            navigate(`/workspace/manufacturing/work-order/${woId}`);
        } catch (error) {
            if (error.name === 'ValidationError') {
                const errors = error.errors || [];
                const firstError = errors[0]?.message || "Please fill in all required fields";
                Message.warning(firstError);
            } else {
                Message.error(error.message || "Failed to update Work Order");
            }
        } finally {
            setLoading(false);
        }
    };

    const finishedItemColumns = [
        { title: "Item Name", dataIndex: "itemName" },
        { title: "Description", dataIndex: "description" },
        { title: "WO Qty", dataIndex: "quantity", render: (val) => <Number value={val} /> },
        { title: "UOM", dataIndex: "uom" },
    ];

    const rawItemColumns = [
        { title: "Item Name", dataIndex: "itemName" },
        { title: "Description", dataIndex: "description" },
        { title: "WO Qty", dataIndex: "quantity", render: (val) => <Number value={val} /> },
        { title: "UOM", dataIndex: "uom" },
    ];

    return (
        <Card title="Edit Work Order" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Save</Button>
                <Button onClick={() => navigate(`/workspace/manufacturing/work-order/${woId}`)}>Cancel</Button>
            </Space>
        }>
            <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Row gutter={24}>
                    <Col span={8}>
                        <FormItem label="BOM:" field="bomId" rules={[{ required: true, message: 'BOM is required' }]}>
                            <Select placeholder="Select BOM" showSearch allowClear
                                onChange={onBomSelected}
                                filterOption={(inputValue, option) =>
                                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0}>
                                {boms.sort((a, b) => a.bomName.localeCompare(b.bomName)).map(bom => (
                                    <Select.Option key={bom.id} value={bom.id}>{bom.bomName}</Select.Option>
                                ))}
                            </Select>
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem label="Quantity:" field="quantity" rules={[{ required: true, message: 'Quantity is required' }]}>
                            <InputNumber min={0} placeholder="Quantity" onChange={onQuantityChange} style={{ width: "100%" }} />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem label="Production Date:" field="productionDate" rules={[{ required: true, message: 'Production Date is required' }]} labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                            <DatePicker style={{ width: "100%" }} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={8}>
                        <FormItem label="Waste Factor:" field="wasteFactor">
                            <InputNumber suffix="%" min={0} max={100} onChange={onWasteFactorChange} style={{ width: "100%" }} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <FormItem label="Notes:" field="notes" labelCol={{ span: 2 }} wrapperCol={{ span: 22 }}>
                            <Input.TextArea placeholder="Notes" autoSize={{ minRows: 2 }} />
                        </FormItem>
                    </Col>
                </Row>

                <Divider />

                <div style={{ marginBottom: 16 }}>
                    <Title heading={6} style={{ margin: 0 }}>Finished Items</Title>
                </div>
                <Table
                    rowKey="itemId"
                    data={finishedItems}
                    columns={finishedItemColumns}
                    pagination={false}
                />

                <Divider />

                <div style={{ marginBottom: 16, marginTop: 24 }}>
                    <Title heading={6} style={{ margin: 0 }}>Raw Items</Title>
                </div>
                <Table
                    rowKey="itemId"
                    data={rawItems}
                    columns={rawItemColumns}
                    pagination={false}
                />
            </Form>
        </Card>
    );
};
